import React from 'react';
import { Table } from 'Shared/components';
import type { BlockListRow } from 'BlockList/types';
import {
  ATTRIBUTE_TYPES,
  REASON_TOKENS,
  SOURCE_TOKENS,
} from 'BlockList/constants';
import { DateCell } from './cells/DateCell';
import { PillCell } from './cells/PillCell';
import { LinkCell } from './cells/LinkCell';
import { NotesCell } from './cells/NotesCell';
import { ActiveCell } from './cells/ActiveCell';
import { AttributeValueCell } from './cells/AttributeValueCell';
import styles from './BlockListTable.module.css';

const HEADERS = [
  'Created At',
  'Attribute Type',
  'Attribute Value',
  'Linked Policy',
  'Reason',
  'Added Via',
  'Updated At',
  'Notes',
  'Active',
];

const COLUMN_WIDTHS = [
  '110px',
  1,
  2,
  1,
  '200px',
  '110px',
  '110px',
  '60px',
  '80px',
];

const COLUMN_CLASSNAMES = [
  '',
  '',
  '',
  '',
  '',
  '',
  '',
  `${styles.centerColumn} ${styles.notesColumn}`,
  '',
];

type BlockListTableProps = {
  rows: BlockListRow[];
  loading?: boolean;
  onDeactivate: (row: BlockListRow) => void;
};

type BlockListTableComponent = React.FC<BlockListTableProps> & {
  Cell: {
    Date: typeof DateCell;
    Pill: typeof PillCell;
    Link: typeof LinkCell;
    Notes: typeof NotesCell;
    Active: typeof ActiveCell;
  };
};

const BlockListTableBase: React.FC<BlockListTableProps> = ({
  rows,
  loading,
  onDeactivate,
}) => {
  return (
    <Table
      headers={HEADERS}
      columnWidths={COLUMN_WIDTHS}
      columnClassNames={COLUMN_CLASSNAMES}
      name="block-list-table"
      className={styles.tableRoot}
      loading={loading}
      hasPagination
      rows={rows.map((row) => {
        const reasonToken = REASON_TOKENS[row.reason];
        const sourceToken = SOURCE_TOKENS[row.source];
        const attributeTypeLabel = ATTRIBUTE_TYPES[row.attributeType].label;

        // Display the policy_number only; the link target uses policy_id
        // because the /policies route is UUID-keyed.
        const firstPolicy = row.blockedPolicies[0];
        const policyNumber = firstPolicy?.policyNumber ?? null;
        const policyId = firstPolicy?.policyId ?? null;
        const policyCell =
          policyNumber == null ? (
            '—'
          ) : policyId != null ? (
            <LinkCell key="policy" href={`/policies/${policyId}`}>
              {policyNumber}
            </LinkCell>
          ) : (
            policyNumber
          );

        // Row-level opacity would cascade into the Notes tooltip popover —
        // dim each cell's content individually instead. The Notes icon is
        // dimmed via a prop so the popover-wrapper sibling stays opaque.
        const isInactive = !row.active;
        const dim = (node: React.ReactNode): React.ReactNode =>
          isInactive ? (
            <span className={styles.dimmedContent}>{node}</span>
          ) : (
            node
          );

        return {
          key: row.id,
          data: [
            dim(<DateCell key="created-at" value={row.createdAt} />),
            dim(attributeTypeLabel),
            dim(
              <AttributeValueCell
                key="attribute-value"
                attributeType={row.attributeType}
                value={row.value}
              />
            ),
            dim(policyCell),
            dim(
              <PillCell
                key="reason"
                label={reasonToken.label}
                className={reasonToken.className}
              />
            ),
            dim(
              <PillCell
                key="source"
                label={sourceToken.label}
                className={sourceToken.className}
              />
            ),
            dim(<DateCell key="updated-at" value={row.updatedAt} />),
            {
              component: (
                <NotesCell
                  key="notes"
                  notes={row.notes}
                  createdAt={row.createdAt}
                  createdByType={row.createdByType}
                  isInactive={isInactive}
                />
              ),
              allowOverflow: true,
            },
            <ActiveCell key="active" row={row} onDeactivate={onDeactivate} />,
          ],
        };
      })}
    />
  );
};

export const BlockListTable = Object.assign(BlockListTableBase, {
  Cell: {
    Date: DateCell,
    Pill: PillCell,
    Link: LinkCell,
    Notes: NotesCell,
    Active: ActiveCell,
  },
}) as BlockListTableComponent;
